import { useConnections } from '@integration-app/react';
import { ChatCircle, Envelope, Phone } from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from 'src/components/Card';
import {
  TagMultiSelect,
  Option as TagOption,
} from 'src/components/TagMultiSelect';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Number, NumberTags } from 'src/interfaces/number.interface';
import { CommunicationChannel } from 'src/services/campaigns/campaigns.interface';
import { processPhoneNumber } from 'src/utils/number';
import { buildEncodedTagOptions } from 'src/components/tag/tag.helpers';
import { useWizard } from '../../../components/wizard/WizardContext';
import { EmailProviderSelect } from './EmailProviderSelect';
import { SmsProviderSelect } from './SmsProviderSelect';
import { QueryObject } from 'src/interfaces/queryObject.interface';
interface OutboundConfigurationsProps {
  className?: string;
}

export const OutboundConfigurations = ({
  className = '',
}: OutboundConfigurationsProps) => {
  const { campaignAutomation, setCampaignAutomation } = useWizard();

  const { get } = useAuthedRequest();
  const { connections: integrationAppConnections } = useConnections();

  const [numbers, setNumbers] = useState<Number[]>([]);
  const [loadingNumbers, setLoadingNumbers] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<TagOption[]>(
    campaignAutomation.fromNumberOptions,
  );
  const [tagValues, setTagValues] = useState<string[]>([]);

  const fetchNumbers = useCallback(async () => {
    setLoadingNumbers(true);
    try {
      const data = await get(`/numbers`);
      setNumbers(data.items);
    } catch (error) {
      console.error('Failed to fetch numbers:', error);
    } finally {
      setLoadingNumbers(false);
    }
  }, [get]);

  const fetchNumberTags = useCallback(async () => {
    setLoadingTags(true);
    try {
      const queryObject: QueryObject = {
        page: '1',
        size: '10',
      };
      const params = new URLSearchParams(queryObject as Record<string, string>);
      const data: NumberTags = await get(`/numbers/tags?${params.toString()}`);
      setTagValues(data.items || []);
    } catch (error) {
      console.error('Failed to fetch number tags:', error);
    } finally {
      setLoadingTags(false);
    }
  }, [get]);

  useEffect(() => {
    fetchNumbers();
    fetchNumberTags();
  }, [fetchNumberTags, fetchNumbers]);

  const tagOptions = useMemo<TagOption[]>(
    () => buildEncodedTagOptions(tagValues),
    [tagValues],
  );

  const numberOptions = useMemo<TagOption[]>(
    () =>
      numbers.map((n) => ({
        value: n.number,
        label: processPhoneNumber(n.number),
      })),
    [numbers],
  );

  const combinedOptions = useMemo<TagOption[]>(
    () => [...tagOptions, ...numberOptions].slice(0, 10),
    [numberOptions, tagOptions],
  );

  const hasOutreachByType = (type: CommunicationChannel) => {
    return (
      campaignAutomation.followUpCommunications.length > 0 &&
      campaignAutomation.followUpCommunications.some(
        (c) => c.contactVia === type,
      )
    );
  };

  const isValidEmailConfig = useMemo(() => {
    const emailFollowUps = campaignAutomation.followUpCommunications.filter(
      (comm) => comm.contactVia === CommunicationChannel.EMAIL,
    );

    if (emailFollowUps.length === 0) return true;

    // Check if campaign-level email provider is set and authenticated
    if (!campaignAutomation.emailProviderOption?.value) return false;

    const connection = integrationAppConnections.find(
      (conn) =>
        conn.integration?.key ===
          campaignAutomation.emailProviderOption?.value && !conn.disconnected,
    );

    return !!connection;
  }, [
    integrationAppConnections,
    campaignAutomation.emailProviderOption,
    campaignAutomation.followUpCommunications,
  ]);

  const isValidSmsConfig = useMemo(() => {
    const smsFollowUps = campaignAutomation.followUpCommunications.filter(
      (comm) => comm.contactVia === CommunicationChannel.SMS,
    );

    if (smsFollowUps.length === 0) return true;

    // Check if campaign-level SMS provider is set and authenticated
    if (!campaignAutomation.smsProviderOption?.value) return false;

    const connection = integrationAppConnections.find(
      (conn) =>
        conn.integration?.key === campaignAutomation.smsProviderOption?.value &&
        !conn.disconnected,
    );

    return !!connection;
  }, [
    integrationAppConnections,
    campaignAutomation.smsProviderOption,
    campaignAutomation.followUpCommunications,
  ]);

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-neutral-700 mb-2">
          Outbound Configurations
        </h2>
        <p className="text-neutral-600">
          Set up the numbers and addresses your campaign will use to reach
          contacts
        </p>
      </div>

      {/* Phone Calls Section */}
      <Card className="p-6 bg-neutral-50 border-neutral-100">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-primary-100 rounded-lg">
            <Phone size={20} className="text-primary-600" />
          </div>
          <h3 className="text-lg font-semibold text-neutral-700">
            For phone calls
          </h3>
        </div>

        <div className="space-y-4">
          {/* Select from numbers */}
          <TagMultiSelect
            disabled={loadingNumbers || loadingTags}
            label="Select from numbers"
            selectedOptions={selectedOptions}
            onChange={(options) => {
              setSelectedOptions(options);
              setCampaignAutomation({
                ...campaignAutomation,
                fromNumberOptions: [...options],
              });
            }}
            options={combinedOptions}
            placeholder="Select phone number or tags..."
            tagColor="primary"
            maxVisibleTags={3}
            required
          />
        </div>
      </Card>

      {/* Email Section */}
      {hasOutreachByType(CommunicationChannel.EMAIL) && (
        <Card className="p-6 bg-neutral-50 border-neutral-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Envelope size={20} className="text-primary-600" />
            </div>
            <h3 className="text-lg font-semibold text-neutral-700">
              For emails
            </h3>
          </div>

          <EmailProviderSelect
            onChange={(provider) => {
              setCampaignAutomation({
                ...campaignAutomation,
                emailProviderOption: provider,
              });
            }}
            isAuthenticated={isValidEmailConfig}
          />

          <p className="text-sm text-warning-500 mt-1">
            Warning: Sending large volumes of emails may result in your domain
            being flagged as spam.
          </p>
        </Card>
      )}

      {/* SMS Messages Section */}
      {hasOutreachByType(CommunicationChannel.SMS) && (
        <Card className="p-6 bg-neutral-50 border-neutral-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-primary-100 rounded-lg">
              <ChatCircle size={20} className="text-primary-600" />
            </div>
            <h3 className="text-lg font-semibold text-neutral-700">
              For SMS Messages
            </h3>
          </div>

          <SmsProviderSelect
            onChange={(provider) => {
              setCampaignAutomation({
                ...campaignAutomation,
                smsProviderOption: provider,
              });
            }}
            isAuthenticated={isValidSmsConfig}
          />

          <p className="text-sm text-warning-500 mt-1">
            Warning: Sending large volumes of SMS messages may result in your
            numbers being flagged as spam.
          </p>
        </Card>
      )}
    </div>
  );
};
